import React, { useEffect, useState, lazy, Suspense, useRef } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { useMsal } from '@azure/msal-react'
import { createIgnisTheme } from './design-system/theme/ignisTheme'
// Shell / auth-path components — needed immediately, kept in the initial bundle.
import LandingPage from './pages/LandingPage'
import Login from './pages/Login'
import MainLayout from './components/layout/MainLayout'
import { OrgSelector } from './components/onboarding/OrgSelector'
import { organizationApi } from './services/organizationApi'
import { useOrgStore } from './store/orgStore'
// Note: Test/demo pages removed - files don't exist in codebase
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { NotificationToast } from './components/notifications/NotificationToast'
import { createAuthApiService, getAuthApiService } from './services/authApi'
import { startSessionRefresh, stopSessionRefresh } from './services/sessionRefreshScheduler'
import { SessionExpiredModal } from './components/auth/SessionExpiredModal'
import { AZURE_AD_SCOPE_STRING } from './config/auth'
import { ArcherLoader } from './design-system/components'
import { RequireOrg } from './components/auth/RequireOrg'
import { CurrentUserIdProvider } from './contexts/CurrentUserIdContext'
import { isValidWorkspaceId } from './utils/workspaceId'

// Route pages — lazy-loaded so the Azure auth callback (a full page reload) only
// downloads the app shell, not every page in the SPA. (#218)
const LiquidGlassDemo = lazy(() => import('./components/demo/LiquidGlassDemo').then(m => ({ default: m.LiquidGlassDemo })))
const Workspaces = lazy(() => import('./pages/Workspaces'))
const Storybooks = lazy(() => import('./pages/Projects'))
const Estate = lazy(() => import('./pages/Estate'))
const ImportProgress = lazy(() => import('./pages/ImportProgress'))
const StorybookDetail = lazy(() => import('./pages/StorybookDetail'))
const Analytics = lazy(() => import('./pages/Analytics'))
const ComplianceDashboard = lazy(() => import('./components/dashboard/ComplianceDashboard'))
const Templates = lazy(() => import('./pages/Templates'))
const Integrations = lazy(() => import('./pages/Integrations'))
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'))
const Settings = lazy(() => import('./pages/Settings'))
const Specifications = lazy(() => import('./pages/Specifications'))
const TemplateSpecifications = lazy(() => import('./pages/admin/TemplateSpecifications').then(m => ({ default: m.TemplateSpecifications })))
const TemplateManagementNew = lazy(() => import('./pages/admin/TemplateManagementNew').then(m => ({ default: m.TemplateManagementNew })))
const Tools = lazy(() => import('./pages/Tools'))
const ToolPlayground = lazy(() => import('./pages/ToolPlayground'))
const CodeIntelligencePlayground = lazy(() => import('./pages/CodeIntelligencePlayground'))
const FlowArchitectureDemo = lazy(() => import('./pages/FlowArchitectureDemo'))
const UserFlowsExplorer = lazy(() => import('./pages/UserFlowsExplorer'))
const UIFlowPlayground = lazy(() => import('./pages/UIFlowPlayground'))
const AICanvas = lazy(() => import('./pages/AICanvas'))
const TranspilationPlayground = lazy(() => import('./pages/TranspilationPlayground'))
const OrgSettings = lazy(() => import('./pages/OrgSettings'))
const ObservabilityDashboard = lazy(() => import('./pages/ObservabilityDashboard'))
const AgenticDevDashboard = lazy(() => import('./pages/AgenticDevDashboard'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'))
const SystemStatusRedirect = lazy(() => import('./pages/SystemStatusRedirect'))
const AssessmentReport = lazy(() => import('./pages/AssessmentReport'))
const MeetTheTeam = lazy(() => import('./pages/MeetTheTeam'))
const WavePlanPage = lazy(() => import('./pages/WavePlanPage').then(m => ({ default: m.WavePlanPage })))
const AgentRunPage = lazy(() => import('./pages/AgentRunPage'))
const BootstrapSetup = lazy(() => import('./pages/BootstrapSetup'))
const AccessPending = lazy(() => import('./pages/AccessPending'))
const MyFeedback = lazy(() => import('./pages/MyFeedback'))
const FeedbackOperations = lazy(() => import('./pages/FeedbackOperations'))
const Deployments = lazy(() => import('./pages/Deployments'))
const PrivateShareViewer = lazy(() => import('./pages/PrivateShareViewer'))

// Generate cryptographically random string for OAuth state/nonce
function generateRandomString(length = 32): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Function to parse Azure auth results from URL fragment
const parseAuthResultFromUrl = (hash: string) => {
  const params = new URLSearchParams(hash.substring(1))
  return {
    accessToken: params.get('access_token'),
    idToken: params.get('id_token'),
    expiresIn: params.get('expires_in'),
    tokenType: params.get('token_type'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }
}

const POST_LOGIN_REDIRECT_KEY = 'ignis_post_login_redirect'
const LOGIN_ERROR_KEY = 'ignis_login_error'

function sanitizeRedirectTarget(target: string | null): string | null {
  if (!target) return null
  if (!target.startsWith('/') || target.startsWith('//')) return null
  return target
}

const LandingPageOrCallback: React.FC<{
  user: any
  setUser: (user: any) => void
}> = ({ user, setUser }) => {
  const navigate = useNavigate()
  // Orgs handed back by /api/auth/login, used by the post-login org check to
  // skip a redundant listMyOrgs round trip (#218). Null → fetch as before.
  const loginOrgsRef = useRef<any[] | null>(null)
  const [isProcessingAuth, setIsProcessingAuth] = React.useState(
    () => {
      const hash = window.location.hash
      return !!(hash && (hash.includes('id_token') || hash.includes('error=')))
    }
  )
  const [authProcessed, setAuthProcessed] = React.useState(false)

  useEffect(() => {
    const handleAuthCallback = async () => {
      const hash = window.location.hash
      const isAzureCallbackHash = hash && (hash.includes('id_token') || hash.includes('error='))

      if (isAzureCallbackHash && !authProcessed) {
        console.log('🔍 Processing authentication callback...')
        setIsProcessingAuth(true)
        setAuthProcessed(true)

        try {
          // Parse tokens/errors from URL fragment
          const urlTokens = parseAuthResultFromUrl(hash)
          console.log('🎫 Extracted tokens:', {
            hasAccessToken: !!urlTokens.accessToken,
            hasIdToken: !!urlTokens.idToken,
            expiresIn: urlTokens.expiresIn,
            hasError: !!urlTokens.error,
          })

          if (urlTokens.error) {
            throw new Error(urlTokens.errorDescription || `Azure AD error: ${urlTokens.error}`)
          }

          if (!urlTokens.idToken) {
            throw new Error('No ID token received from Azure AD')
          }

          // Validate state parameter to prevent CSRF attacks
          const expectedState = sessionStorage.getItem('oauth_state')
          if (expectedState && urlTokens.state !== expectedState) {
            sessionStorage.removeItem('oauth_state')
            sessionStorage.removeItem('oauth_nonce')
            throw new Error('OAuth state mismatch — possible CSRF attack. Please try signing in again.')
          }
          sessionStorage.removeItem('oauth_state')
          sessionStorage.removeItem('oauth_nonce')

          console.log('🔑 ID token received from Azure AD')

          // Send ID token to backend to get session JWT
          console.log('📞 Sending ID token to backend /api/auth/login...')
          const authService = getAuthApiService()
          const loginData = await authService.authenticateWithIdToken(urlTokens.idToken)
          console.log('✅ Login successful:', loginData)

          // Store user data
          const dbUser = loginData.data.user
          localStorage.setItem('ignis_user', JSON.stringify(dbUser))
          localStorage.setItem('ignis_authenticated', 'true')

          // Capture orgs folded into the login response so the org check below
          // can skip a separate listMyOrgs round trip (#218).
          loginOrgsRef.current = Array.isArray(loginData.data.organizations)
            ? loginData.data.organizations
            : null

          console.log('✅ Authentication successful!')
          console.log('✅ User data with DB ID:', {
            id: dbUser.id,
            email: dbUser.email,
            displayName: dbUser.displayName
          })

          // Clean up URL hash
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)

          // Notify parent - this will trigger org check and redirect.
          // (No artificial delay needed: authenticateWithIdToken stores the session
          // token synchronously into localStorage before it resolves above, and API
          // clients read it synchronously — so there is no token-availability race.)
          setUser(dbUser)

          // Keep isProcessingAuth true while redirecting
          // It will be set to false by the redirect navigation

        } catch (error) {
          console.error('❌ Authentication error:', error)
          sessionStorage.removeItem('oauth_state')
          sessionStorage.removeItem('oauth_nonce')
          sessionStorage.setItem(
            LOGIN_ERROR_KEY,
            error instanceof Error ? error.message : 'Authentication failed. Please try again.'
          )
          localStorage.removeItem('ignis_user')
          localStorage.removeItem('ignis_authenticated')
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
          try {
            const authService = getAuthApiService()
            authService.clearAuthToken()
          } catch (authError) {
            console.warn('Failed to clear auth token after login error:', authError)
            localStorage.removeItem('ignis_auth_token')
          }
          setIsProcessingAuth(false)
          setAuthProcessed(false)
          navigate('/login', { replace: true })
        }
      }
    }

    handleAuthCallback()
  }, [authProcessed, setUser])

  // Navigate after login — org check determines destination
  useEffect(() => {
    if (!user || !isProcessingAuth) return

    const checkOrgAndRedirect = async () => {
      const pendingRedirect = sanitizeRedirectTarget(sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY))
      if (pendingRedirect) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
        navigate(pendingRedirect, { replace: true })
        return
      }

      try {
        // Prefer orgs handed back by /api/auth/login (saves a round trip, #218);
        // fall back to a fetch if the login response didn't include them.
        const preloadedOrgs = loginOrgsRef.current
        loginOrgsRef.current = null
        console.log('🔍 Checking organizations for user:', user.email)
        const orgs = preloadedOrgs ?? await organizationApi.listMyOrgs()
        console.log('📊 Found', orgs.length, 'organization(s)', preloadedOrgs ? '(from login response)' : '(fetched)')

        const { setActiveOrg, setOrgs } = useOrgStore.getState()
        setOrgs(orgs)

        if (orgs.length === 0) {
          // Check if user is bootstrap admin
          console.log('🔍 No orgs found, checking if bootstrap admin...')
          const isBootstrap = await organizationApi.isBootstrapAdmin(user.email)
          console.log('🔐 Bootstrap admin check result:', isBootstrap)

          if (isBootstrap) {
            console.log('✅ Redirecting to bootstrap setup')
            navigate('/bootstrap-setup', { replace: true })
          } else {
            console.log('🧭 Redirecting to onboarding')
            navigate('/onboarding', { replace: true })
          }
        } else if (orgs.length === 1) {
          console.log('✅ Setting active org:', orgs[0].name)
          setActiveOrg(orgs[0])
          navigate('/workspaces', { replace: true })
        } else {
          console.log('🔀 Multiple orgs, redirecting to selector')
          navigate('/org-select', { replace: true })
        }
      } catch (error) {
        console.error('❌ Org check failed:', error)
        // Fallback: go to workspaces if org check fails
        navigate('/workspaces', { replace: true })
      }
    }

    checkOrgAndRedirect()
  }, [user, isProcessingAuth, navigate])

  // Show loader while processing auth
  if (isProcessingAuth) {
    return (
      <ArcherLoader
        fullscreen
        size={260}
        message="Completing authentication..."
      />
    )
  }

  // If user is authenticated, redirect to dashboard
  if (user) {
    return <Navigate to="/workspaces" replace />
  }

  // Navigate to login page when user clicks "Get Started"
  const handleGetStarted = () => {
    navigate('/login')
  }

  return (
    <LandingPage
      onGetStarted={handleGetStarted}
    />
  )
}

const App: React.FC = () => {
  // Get MSAL instance for auth service initialization
  const { instance: msalInstance } = useMsal()

  // Single enterprise theme — no dark/light switching

  // Authentication state
  const [user, setUser] = useState<any>(null)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  // Initialize auth service when MSAL instance is available
  useEffect(() => {
    if (msalInstance) {
      createAuthApiService(msalInstance as any)
    }
  }, [msalInstance])



  const theme = createIgnisTheme()

  // Azure AD Login Handler
  const handleLogin = () => {
    console.log('🚀 Starting Azure AD authentication...')

    // Get config from runtime config (Docker entrypoint) with fallback to Vite env vars
    const runtimeConfig = (window as any).runtimeConfig || {}

    const clientId = runtimeConfig.AZURE_AD_CLIENT_ID ||
      import.meta.env.VITE_AZURE_AD_CLIENT_ID ||
      'e39e6a7c-055e-4811-8c43-fa29c99e57c8'
    const tenantId = runtimeConfig.AZURE_AD_TENANT_ID ||
      import.meta.env.VITE_AZURE_AD_TENANT_ID ||
      '487753ef-8cf6-4960-ad5f-9ceb7b82fcd7'
    const redirectUri = runtimeConfig.AZURE_AD_REDIRECT_URI ||
      import.meta.env.VITE_AZURE_AD_REDIRECT_URI ||
      window.location.origin
    const scopes = runtimeConfig.AZURE_AD_SCOPES ||
      import.meta.env.VITE_AZURE_AD_SCOPES ||
      AZURE_AD_SCOPE_STRING
    const normalizedScopes = scopes
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter(Boolean)
      .join(' ')

    const encodedRedirectUri = encodeURIComponent(redirectUri)
    const encodedScope = encodeURIComponent(normalizedScopes)

    // Generate cryptographically random state and nonce to prevent CSRF and token replay
    const state = generateRandomString()
    const nonce = generateRandomString()
    sessionStorage.setItem('oauth_state', state)
    sessionStorage.setItem('oauth_nonce', nonce)

    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=id_token&` +
      `redirect_uri=${encodedRedirectUri}&` +
      `scope=${encodedScope}&` +
      `response_mode=fragment&` +
      `state=${encodeURIComponent(state)}&` +
      `nonce=${encodeURIComponent(nonce)}`

    console.log('🔗 Redirecting to Azure AD:', authUrl)
    console.log('📍 Using redirect URI:', redirectUri)
    console.log('📋 Runtime config available:', !!runtimeConfig.AZURE_AD_CLIENT_ID)
    console.log('💡 If this fails, you need to add this redirect URI to your Azure AD app registration')

    window.location.href = authUrl
  }

  // Start / stop the proactive token-refresh scheduler whenever auth state changes
  useEffect(() => {
    if (user) {
      startSessionRefresh()
    } else {
      stopSessionRefresh()
    }
  }, [user])

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Don't check auth if we're on the callback route (will be handled by AuthCallbackHandler)
      if (window.location.hash && window.location.hash.includes('id_token')) {
        setIsCheckingAuth(false)
        return
      }

      // Clean up any corrupted workspace data in localStorage
      // This prevents 404 errors when the workspace ID is not a supported UUID or CUID.
      const savedWorkspace = localStorage.getItem('ignis_active_workspace')
      if (savedWorkspace) {
        try {
          const workspace = JSON.parse(savedWorkspace)
          if (!isValidWorkspaceId(workspace?.id)) {
            console.warn('🧹 Clearing corrupted workspace from localStorage:', workspace?.id)
            localStorage.removeItem('ignis_active_workspace')
          }
        } catch {
          console.warn('🧹 Clearing malformed workspace data from localStorage')
          localStorage.removeItem('ignis_active_workspace')
        }
      }

      const authService = getAuthApiService()
      const savedUser = localStorage.getItem('ignis_user')

      if (!savedUser) {
        if (authService.isAuthenticated()) {
          try {
            const dbUser = await authService.getCurrentUser()
            setUser(dbUser)
            localStorage.setItem('ignis_user', JSON.stringify(dbUser))
            localStorage.setItem('ignis_authenticated', 'true')
          } catch (error) {
            console.warn('Failed to restore authenticated session:', error)
            localStorage.removeItem('ignis_authenticated')
            authService.clearAuthToken()
            setUser(null)
          }
        }
        setIsCheckingAuth(false)
        return
      }

      try {
        JSON.parse(savedUser)
        if (!authService.isAuthenticated()) {
          localStorage.removeItem('ignis_user')
          localStorage.removeItem('ignis_authenticated')
          authService.clearAuthToken()
          setUser(null)
          setIsCheckingAuth(false)
          return
        }

        try {
          const dbUser = await authService.getCurrentUser()
          setUser(dbUser)
          localStorage.setItem('ignis_user', JSON.stringify(dbUser))
          localStorage.setItem('ignis_authenticated', 'true')
        } catch (error) {
          console.warn('Failed to refresh user from backend, clearing cached auth:', error)
          localStorage.removeItem('ignis_user')
          localStorage.removeItem('ignis_authenticated')
          authService.clearAuthToken()
          setUser(null)
        }
      } catch (error) {
        console.warn('Failed to parse saved user:', error)
        localStorage.removeItem('ignis_user')
        localStorage.removeItem('ignis_authenticated')
        authService.clearAuthToken()
        setUser(null)
      }
      setIsCheckingAuth(false)
    }

    checkAuth()
  }, [])

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ArcherLoader
          fullscreen
          size={280}
          message="Acquiring target..."
        />
      </ThemeProvider>
    )
  }

  // Check if demo mode is requested
  const urlParams = new URLSearchParams(window.location.search)
  const isDemoMode = urlParams.get('demo') === 'true'
  const showDevPreviewRoutes = import.meta.env.DEV

  if (isDemoMode) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LiquidGlassDemo />
      </ThemeProvider>
    )
  }

  // Wrap everything in Router
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <CurrentUserIdProvider key={user?.id ?? 'signed-out'} value={user?.id ?? null}>
        <WorkspaceProvider enabled={!isCheckingAuth && Boolean(user)}>
          <NotificationProvider>
          <NotificationToast />
          {/* Session expiry modal — mounted once; only active when authenticated */}
          {user && <SessionExpiredModal onSignIn={handleLogin} />}
          <Suspense fallback={<ArcherLoader fullscreen size={260} />}>
          <Routes>
          {/* Landing page / Auth callback route */}
          <Route
            path="/"
            element={
              <LandingPageOrCallback
                user={user}
                setUser={setUser}
              />
            }
          />

          {/* Auth callback route - handles Azure AD redirect with token in hash fragment */}
          <Route
            path="/auth/callback/azure-ad"
            element={
              <LandingPageOrCallback
                user={user}
                setUser={setUser}
              />
            }
          />

          {/* Login page - accessible when not authenticated */}
          {!user && (
            <Route path="/login" element={<Login />} />
          )}

          {/* Public invitation accept page — works both authenticated and unauthenticated */}
          <Route path="/invitations/:token" element={<AcceptInvitation />} />

          {/* Public shell for purpose-limited bearer links. Content remains behind token validation. */}
          <Route path="/share/:token" element={<PrivateShareViewer />} />

          {/* Status page lives on its own subdomain now (status.<your-domain>).
              The standalone archer-status service serves it out-of-band so it
              survives the main app being down. This route is a redirect-only
              landing — see SystemStatusRedirect.tsx. */}
          <Route path="/status" element={<SystemStatusRedirect />} />

          {/* Development-only route for reviewing the Agentic Dev mockup without auth/org setup. */}
          {showDevPreviewRoutes && (
            <Route path="/agentic-dev/preview" element={<AgenticDevDashboard mode="mock" />} />
          )}

          {/* Onboarding and org selector — accessible when authenticated */}
          {user && (
            <>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/bootstrap-setup" element={<BootstrapSetup />} />
              <Route path="/access-pending" element={<AccessPending />} />
              <Route path="/org-select" element={<OrgSelector />} />
            </>
          )}

          {/* Protected routes - only accessible when authenticated AND user has an org */}
          {user && (
            <Route element={<RequireOrg />}>
              {/* Redirect legacy /dashboard to /workspaces */}
              <Route path="/dashboard" element={<Navigate to="/workspaces" replace />} />

              <Route path="/workspaces" element={
                <MainLayout>
                  <Workspaces />
                </MainLayout>
              } />
              <Route path="/projects" element={
                <MainLayout>
                  <Storybooks />
                </MainLayout>
              } />
              <Route path="/estate" element={
                <MainLayout>
                  <Estate />
                </MainLayout>
              } />
              <Route path="/estate/imports/:workspaceId/:jobId?" element={
                <MainLayout>
                  <ImportProgress actorId={user.id} />
                </MainLayout>
              } />
              <Route path="/projects/:storybookId" element={
                <MainLayout>
                  <StorybookDetail />
                </MainLayout>
              } />
              <Route path="/projects/:storybookId/assessment-report" element={
                <AssessmentReport />
              } />

              <Route path="/projects/:storybookId/wave-plan" element={
                <MainLayout>
                  <WavePlanPage />
                </MainLayout>
              } />


              <Route path="/runs/:runId" element={
                <MainLayout>
                  <AgentRunPage />
                </MainLayout>
              } />

              <Route path="/projects/:storybookId/compliance" element={
                <MainLayout>
                  <ComplianceDashboard />
                </MainLayout>
              } />

              <Route path="/analytics" element={
                <MainLayout>
                  <Analytics />
                </MainLayout>
              } />
              <Route path="/meet-the-team" element={
                <MainLayout>
                  <MeetTheTeam />
                </MainLayout>
              } />
              <Route path="/templates" element={
                <MainLayout>
                  <Templates />
                </MainLayout>
              } />
              <Route path="/admin/templates/:templateId/manage" element={
                <MainLayout>
                  <TemplateManagementNew />
                </MainLayout>
              } />
              <Route path="/admin/templates/:templateId/specifications" element={
                <MainLayout>
                  <TemplateSpecifications />
                </MainLayout>
              } />
              <Route path="/specifications" element={
                <MainLayout>
                  <Specifications />
                </MainLayout>
              } />
              <Route path="/integrations" element={
                <MainLayout>
                  <Integrations />
                </MainLayout>
              } />
              <Route path="/feedback/mine" element={
                <MainLayout>
                  <MyFeedback />
                </MainLayout>
              } />
              <Route path="/feedback/ops" element={
                <MainLayout>
                  <FeedbackOperations />
                </MainLayout>
              } />
              <Route path="/deployments" element={
                <MainLayout>
                  <Deployments />
                </MainLayout>
              } />
              <Route path="/knowledge-base/*" element={
                <MainLayout>
                  <KnowledgeBase />
                </MainLayout>
              } />
              <Route path="/settings" element={
                <MainLayout>
                  <Settings />
                </MainLayout>
              } />
              <Route path="/tools" element={
                <MainLayout>
                  <Tools />
                </MainLayout>
              } />
              <Route path="/tools/tool-playground" element={
                <MainLayout>
                  <ToolPlayground />
                </MainLayout>
              } />
              <Route path="/tools/code-intelligence" element={
                <MainLayout>
                  <CodeIntelligencePlayground />
                </MainLayout>
              } />
              <Route path="/tools/flow-architecture" element={
                <MainLayout>
                  <FlowArchitectureDemo />
                </MainLayout>
              } />
              <Route path="/tools/user-flows" element={
                <MainLayout>
                  <UserFlowsExplorer />
                </MainLayout>
              } />
              <Route path="/tools/ui-flow-playground" element={
                <MainLayout>
                  <UIFlowPlayground />
                </MainLayout>
              } />
              <Route path="/tools/transpilation-playground" element={
                <MainLayout>
                  <TranspilationPlayground />
                </MainLayout>
              } />
              <Route path="/ai-canvas" element={
                <MainLayout>
                  <AICanvas />
                </MainLayout>
              } />
              <Route path="/org-settings" element={
                <MainLayout>
                  <OrgSettings />
                </MainLayout>
              } />
              <Route path="/observability" element={
                <MainLayout>
                  <ObservabilityDashboard />
                </MainLayout>
              } />
              <Route path="/agentic-dev" element={
                <MainLayout>
                  <AgenticDevDashboard />
                </MainLayout>
              } />
              {/* Test/demo routes removed - source files don't exist */}

            </Route>
          )}

          {/* Demo route */}
          <Route
            path="/demo"
            element={
              <LiquidGlassDemo />
            }
          />

          {/* Catch all route - redirect based on auth status */}
          <Route path="*" element={<Navigate to={user ? "/workspaces" : "/"} replace />} />
          </Routes>
          </Suspense>
          </NotificationProvider>
        </WorkspaceProvider>
        </CurrentUserIdProvider>
      </Router>
    </ThemeProvider>
  )
}

export default App
